import { FC, useState } from 'react';
import { useParams } from 'react-router';
import styled from '@emotion/styled';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import DescriptionIcon from '@material-ui/icons/Description';
import { DateTime } from 'luxon';

import {
  COLOR_BORDER,
  COLOR_BUTTON_BACKGROUND,
  COLOR_BUTTON_TEXT,
  COLOR_CONTENT_BACKGROUND,
  COLOR_EFFECT,
  COLOR_SHADOW,
  COLOR_TEXT_DISABLE,
  COLOR_TEXT_MAIN,
} from '../../../../theme/colors';
import {
  DeleteTopicDocument,
  DeleteTopicMutation,
  DeleteTopicMutationVariables,
  GetTopicsDocument,
  GetTopicsQuery,
  GetTopicsQueryVariables,
} from '../../../../generated/graphql';
import { lastAccessedOrganizationVar } from '../../../../cache';
import LoadingButton from '../../primitive/LoadingButton';
import { NewThreadDrawer } from './NewThreadDrawer';
import { EditThreadDrawer } from './EditThreadDrawer';
import { ThreadDrawer } from './ThreadDrawer';
import {
  AttachableAttachment,
  AttachableAttachmentWrapper,
  HeaderAvater,
  HeaderDate,
  HeaderUserName,
  PaperThreadHeader,
  Paragraph,
  ThreadImageWrapper,
  ThreadItemWrapper,
} from '../Experiments/Thread/ThreadComponents';
import globalState from '../../../../operations/commands';
import Loading from '../Loading';
import Config from '../../../../core/Config';
import mutationVariables from '../../../../utility/mutationVariables';

type Params = {
  experiment_id: string;
};

/**
 * エクスペリエンスの掲示板
 */
export const Message: FC<any> = () => {
  const lastOrg = useReactiveVar(lastAccessedOrganizationVar);
  const organization_id = lastOrg!.id;
  const { experiment_id } = useParams<Params>();
  const [openNewThread, setOpenNewThread] = useState<boolean>(false);
  const [openEditThread, setOpenEditThread] = useState<boolean>(false);
  const [openThread, setOpenThread] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [topicIndex, setTopicIndex] = useState<number>(-1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const config: Config = (window as any).__hugotconfig__;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleModalOpen = () => setOpenDelete(true);

  const handleModalClose = () => setOpenDelete(false);

  const {
    data,
    refetch,
    loading: loadingTopics,
  } = useQuery<GetTopicsQuery, GetTopicsQueryVariables>(GetTopicsDocument, {
    skip: !organization_id || !experiment_id,
    variables: { project_id: experiment_id! },
    context: { clientName: 'planbcd' },
  });

  const [deleteTopic, { loading: loadingDelete }] = useMutation<DeleteTopicMutation, DeleteTopicMutationVariables>(
    DeleteTopicDocument,
    {
      context: { clientName: 'planbcd' },
      onCompleted: () => {
        refetch();
        setTopicIndex(-1);
      },
      onError: (error) => {
        globalState.setErrors({
          message: `スレッドの削除ができませんでした: ${error.message}`,
          name: 'NonFatalError',
          context: { clientName: 'planbcd' },
        });
      },
    },
  );

  const getTopics = () => {
    refetch();
  };

  return (
    <>
      <Dialog open={openDelete} onClose={handleModalClose}>
        <DialogTitle>トピックの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>トピックを削除します。</DialogContentText>
          <DialogContentText>トピックを削除するともとに戻すことができません。</DialogContentText>
          <DialogContentText>よろしければ次にお進みください。</DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={false}
            onClick={() => {
              handleModalClose();
            }}
          >
            キャンセル
          </SecondaryButton>
          <PrimaryButton
            disabled={false}
            onClick={() => {
              deleteTopic(
                mutationVariables({
                  id: data?.topics[topicIndex] ? data?.topics[topicIndex].id : '',
                }),
              );
              handleModalClose();
            }}
          >
            OK
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <NewThreadDrawer refetch={getTopics} open={openNewThread} setOpen={setOpenNewThread} />
      {data?.topics[topicIndex] ? (
        <EditThreadDrawer
          selectedTopic={data?.topics[topicIndex]}
          refetch={getTopics}
          open={openEditThread}
          setOpen={setOpenEditThread}
        />
      ) : null}
      {data?.topics[topicIndex] ? (
        <ThreadDrawer
          refetch={getTopics}
          topic={data?.topics[topicIndex]}
          open={openThread}
          setOpen={setOpenThread}
          loadingTopics={loadingTopics}
        />
      ) : null}
      <StyledPaper>
        <PaperTitle>トピックを作成する</PaperTitle>
        <ThreadItemWrapper>
          <Paragraph>
            {
              '・投稿された内容は、このページを閲覧できるユーザー全員に共有されます。\n・不適切な内容の場合、サポートスタッフにより削除させていただくこともございます。'
            }
          </Paragraph>
        </ThreadItemWrapper>

        <Borderline />
        {/* ボタン表示エリア */}
        <PaperFooter>
          <PrimaryButton
            disabled={false}
            onClick={() => {
              setOpenNewThread(true);
            }}
          >
            トピックを作成する
          </PrimaryButton>
        </PaperFooter>
      </StyledPaper>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        keepMounted
        open={openMenu}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5,
            width: '20ch',
          },
        }}
      >
        <MenuItem
          disabled={!data?.topics[topicIndex]?.permissions?.update.permit}
          onClick={() => {
            setOpenEditThread(true);
            handleClose();
          }}
        >
          編集
        </MenuItem>
        <MenuItem
          disabled={!data?.topics[topicIndex]?.permissions?.destroy.permit}
          onClick={() => {
            handleModalOpen();
            handleClose();
          }}
        >
          削除
        </MenuItem>
      </Menu>

      {loadingTopics || loadingDelete ? (
        <Loading />
      ) : (
        data?.topics.map((topic, index) => (
          <StyledPaper key={topic.id}>
            <PaperThreadHeader>
              <TopicInfoWrapper>
                <HeaderAvater src={topic.user.avatar_image_url} />
                <HeaderUserName
                  href={`${config.K2_BASE_URL}/users/${topic.user.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {topic.user.username}
                </HeaderUserName>
                <HeaderDate>{DateTime.fromISO(topic.created_at).toFormat('yyyy/MM/dd hh:mm')}</HeaderDate>
              </TopicInfoWrapper>
              <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={(e) => {
                  handleClick(e);
                  setTopicIndex(index);
                }}
              >
                <MoreHorizIcon />
              </IconButton>
            </PaperThreadHeader>
            <PaperTitle>{topic.title}</PaperTitle>
            <ThreadItemWrapper>
              <Paragraph>{topic.comment}</Paragraph>
              {topic.attachables_attachments ? (
                <ThreadImageWrapper>
                  {topic.attachables_attachments.map((data) =>
                    data?.attachment.content_type === 'image/jpeg' || data?.attachment.content_type === 'image/png' ? (
                      <AttachableAttachmentWrapper key={data?.id}>
                        <AttachableAttachment
                          href={data?.attachment.urls.original}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            key={data?.id}
                            style={{ maxHeight: 180 }}
                            alt="thread_img"
                            src={data?.attachment.urls.original}
                          ></img>
                        </AttachableAttachment>
                      </AttachableAttachmentWrapper>
                    ) : (
                      <AttachableAttachmentWrapper key={data?.id}>
                        <DescriptionIcon />
                        <AttachableAttachment
                          href={data?.attachment.urls.original}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {data?.attachment.name}
                        </AttachableAttachment>
                      </AttachableAttachmentWrapper>
                    ),
                  )}
                </ThreadImageWrapper>
              ) : null}
            </ThreadItemWrapper>
            <ThreadDetailsWrapper>
              {topic.comments && topic.comments.length > 0 ? (
                <>
                  <ThreadDetailsAvatarWrapper>
                    {topic.comments
                      .filter(
                        (comment, index, self) => self.findIndex((e) => e?.user.id === comment?.user.id) === index,
                      )
                      .map((comment) => (
                        <ThreadDetailsAvatar key={comment?.id} src={comment?.user.avatar_image_url} />
                      ))}
                    {/* {topic.comments.map((comment) => (
                      <ThreadDetailsAvatar src={comment?.user.avatar_image_url} />
                    ))} */}
                  </ThreadDetailsAvatarWrapper>
                  <ThreadDetailsText
                    onClick={() => {
                      setTopicIndex(index);
                      setOpenThread(true);
                    }}
                  >{`${topic.comments.length}件の返信があります`}</ThreadDetailsText>
                  <ThreadDetailsDate>{`最新の返信 ${DateTime.fromISO(
                    topic.comments[topic.comments.length - 1]?.created_at || topic.updated_at,
                  ).toFormat('yyyy/MM/dd hh:mm')}`}</ThreadDetailsDate>
                </>
              ) : null}
            </ThreadDetailsWrapper>

            <Borderline />
            {/* ボタン表示エリア */}
            <PaperFooter>
              <PrimaryButton
                disabled={false}
                onClick={() => {
                  setTopicIndex(index);
                  setOpenThread(true);
                }}
              >
                詳細を見る
              </PrimaryButton>
            </PaperFooter>
          </StyledPaper>
        ))
      )}
    </>
  );
};

const StyledPaper = styled('div')(`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  background: ${COLOR_CONTENT_BACKGROUND};
  box-shadow: 0px 1px 9px ${COLOR_SHADOW};
  border-radius: 8px;
  width: 100%;
  margin: 0 auto 24px auto;
  color: #4C556C;
  position: relative;
`);

const PaperTitle = styled(Typography)(`
  font-family: Hiragino Sans;
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 27px;
  color: ${COLOR_TEXT_MAIN};
  overflow-wrap: anywhere;
`);

const Borderline = styled('div')(`
  height: 1px;
  border: 0.5px solid ${COLOR_BORDER};
  width: 100%;
  margin: 24px 0;
`);

const PaperFooter = styled('div')(`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`);

const ThreadDetailsWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const ThreadDetailsAvatarWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-right: 4px;
`;

const ThreadDetailsAvatar = styled(Avatar)`
  width: 21px;
  height: 21px;
  margin-right: 8px;
`;

const ThreadDetailsText = styled('span')`
  color: #006bd6;
  font-size: 14px;
  margin-right: 8px;
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ThreadDetailsDate = styled('span')`
  color: #707889;
  font-size: 10px;
`;

const PrimaryButton = styled(LoadingButton)(`
  color: ${COLOR_BUTTON_TEXT};
  height: 40px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 700;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  padding: 12px 24px;
  text-transform: none;
  :hover {
    background: ${COLOR_EFFECT};
    border-color: ${COLOR_EFFECT};
  }
  :disabled {
    background: ${COLOR_BORDER};
    color: ${COLOR_TEXT_DISABLE};
    border: ${COLOR_TEXT_DISABLE};
  }
`);

const SecondaryButton = styled(Button)<{ width?: string }>(`
  color: ${COLOR_TEXT_MAIN};
  height: 40px;
  padding: 13px 16px;
  font-size: 14px;
  background: ${COLOR_BUTTON_BACKGROUND};
  font-weight: 700;
  border: 1.5px solid ${COLOR_BORDER};
  border-radius: 4px;
  margin-right: 16px;
  :hover {
    background: ${COLOR_BUTTON_BACKGROUND};
    color: ${COLOR_EFFECT};
  }
`);

const TopicInfoWrapper = styled('div')`
  display: flex;
  align-items: center;
`;
